
import { defineComponent } from 'vue';
// Components
import AuthView from 'client/containers/user/Views/AuthView.vue';
import DuoUserResetPasswordRedirectHandler from 'client/containers/user/UserResetPassword/UserResetPasswordMfaHandlers/DuoUserResetPasswordRedirectHandler.vue';
import LogoIcon from 'client/components/LogoIcon.vue';
import MfaView from 'client/containers/user/Views/MfaView.vue';
import OtpUserResetPasswordHandler from 'client/containers/user/UserResetPassword/UserResetPasswordMfaHandlers/OtpUserResetPasswordHandler.vue';
import PushUserResetPasswordHandler from 'client/containers/user/UserResetPassword/UserResetPasswordMfaHandlers/PushUserResetPasswordHandler.vue';
import TotpUserResetPasswordHandler from 'client/containers/user/UserResetPassword/UserResetPasswordMfaHandlers/TotpUserResetPasswordHandler.vue';
import UserResetPasswordInputHandler from 'client/containers/user/UserResetPassword/UserResetPasswordInputHandler.vue';
import WebAuthnUserResetPasswordHandler from 'client/containers/user/UserResetPassword/UserResetPasswordMfaHandlers/WebAuthnUserResetPasswordHandler.vue';

// Constants
import { LoginConsts } from 'client/util/Constants';
import {
  SMS_OTP_IDLE_TIMEOUT_STATUS_CODE,
  SMS_OTP_VERIFY_RATE_LIMIT_STATUS_CODE,
  createSmsOtpLoginSession,
} from 'client/util/Constants/smsOtpLoginSession';
import MfaConstants from 'client/containers/Login/MfaConstants';
import type { MfaFactor } from 'client/types/Auth';
import type { SmsOtpLoginSessionPatch } from 'client/util/Constants/smsOtpLoginSession';

// Service Errors
import MfaRequiredError from 'client/services/response/MfaRequiredError';
import RequestError from 'client/services/response/RequestError';

// Utils
import { useMfaLabels } from 'client/util/Constants/MfaLabels';
import ProcessPasswordResetMfaError from 'client/containers/user/MfaComponents/mfaUtils/processPasswordResetMfaError';
import getCurrentComponentNameFromList from 'client/containers/user/MfaComponents/mfaUtils/getCurrentComponentNameFromList';
import getLastUsedFactor from 'client/containers/user/MfaComponents/mfaUtils/getLastUsedFactor';
import mfaFactorBuilder from 'client/containers/user/MfaComponents/mfaUtils/mfaFactorBuilder';
import routerService from 'client/services/routerService';

interface pwdResetForm {
  password: string;
  confirm: string;
  challenge?: any;
  pwdResetToken?: any;
}

const { mfaNames } = MfaConstants;
const {
  responseMessages: {
    mfaUnavailableForPwdReset,
    passwordResetError,
    passwordResetSuccess,
  },
} = LoginConsts;

const componentsUnderControl = [
  {
    id: '',
    componentName: UserResetPasswordInputHandler.name,
  },
  {
    id: mfaNames.duo,
    componentName: DuoUserResetPasswordRedirectHandler.name,
  },
  {
    id: mfaNames.sms_otp,
    componentName: OtpUserResetPasswordHandler.name,
  },
  {
    id: mfaNames.push,
    componentName: PushUserResetPasswordHandler.name,
  },
  {
    id: mfaNames.totp,
    componentName: TotpUserResetPasswordHandler.name,
  },
  {
    id: mfaNames.webauthn,
    componentName: WebAuthnUserResetPasswordHandler.name,
  },
];

const getCurrentComponentName = getCurrentComponentNameFromList(componentsUnderControl);

const formInitialState: pwdResetForm = {
  password: '',
  confirm: '',
};

export default defineComponent({
  name: 'UserResetPasswordController',

  components: {
    AuthView,
    DuoUserResetPasswordRedirectHandler,
    LogoIcon,
    MfaView,
    OtpUserResetPasswordHandler,
    PushUserResetPasswordHandler,
    TotpUserResetPasswordHandler,
    UserResetPasswordInputHandler,
    WebAuthnUserResetPasswordHandler,
  },

  props: {
    mfaType: {
      type: String,
      default: '',
    },
    requestKey: {
      type: String,
      default: '',
    },
    step: {
      type: String,
      default: '',
    },
  },

  setup() {
    const mfaLabels = useMfaLabels();

    return {
      mfaLabels,
    };
  },

  data() {
    return {
      errorMessage: '',
      // using undefined so we display 0
      errorMessageDisplayDuration: undefined as number | undefined,
      factors: [] as MfaFactor[],
      form: {
        ...formInitialState,
      },
      smsOtpLoginSession: createSmsOtpLoginSession(),
    };
  },

  computed: {
    componentHandlers() {
      return {
        resetErrorMessage: this.onResetErrorMessage,
        success: this.onSuccess,
        'sms-session-patch': this.patchSmsOtpLoginSession,
      };
    },

    componentName() {
      const id = this.isMfa ? this.mfaType : this.step;

      return getCurrentComponentName(id);
    },

    componentProps() {
      const base = {
        form: this.form,
        hasPendingErrorMessage: this.hasPendingErrorMessage,
        requestKey: this.requestKey,
      };

      if (this.isMfa && this.mfaType === mfaNames.sms_otp) {
        return {
          ...base,
          smsLoginSession: this.smsOtpLoginSession,
        };
      }

      return base;
    },

    mfaFactorsForView(): MfaFactor[] {
      if (!this.smsOtpLoginSession.removedFromChooser) {
        return this.factors;
      }

      return this.factors.filter((f) => f.name !== mfaNames.sms_otp);
    },

    controllingQueryParams() {
      return {
        template: 'resetPasswordUserForm',
        key: this.requestKey,
      };
    },

    hasPendingErrorMessage() {
      const remainingDuration = this.errorMessageDisplayDuration || -1;
      return remainingDuration >= 0;
    },

    headerText() {
      return this.isMfa ? this.mfaLabels.verifyIdentity : this.mfaLabels.passwordResetHeader;
    },

    isMfa() {
      return this.step === 'mfa';
    },
  },

  mounted() {
    this.validateInitialStep();
  },

  methods: {
    handleErrorCountdown() {
      const errorCountdown = setInterval(() => {
        if ((this.errorMessageDisplayDuration as number) < 1) {
          this.onResetErrorMessage();
          clearInterval(errorCountdown);
        }

        (this.errorMessageDisplayDuration as number) -= 1;
      }, 1000);
    },

    handleResetForm() {
      this.form = {
        ...formInitialState,
      };
    },

    onCredentialError(error: unknown) {
      // todo: update error message to respond to action
      if (error instanceof RequestError) {
        return this.setErrorMessage(error.message);
      }
      if (!(error instanceof MfaRequiredError)) {
        return this.setErrorMessage(passwordResetError);
      }

      this.smsOtpLoginSession = createSmsOtpLoginSession();

      this.factors = mfaFactorBuilder(
        {
          factors: error.factors,
        },
        {
          controllingQueryParams: this.controllingQueryParams,
        },
      );

      if (this.factors.length === 0) {
        return this.setErrorMessage(mfaUnavailableForPwdReset);
      }

      this.form.challenge = error.challenge;
      this.form.pwdResetToken = error.pwdResetToken;

      const mfaParam = getLastUsedFactor(this.factors);

      return routerService.goToUserLogin({
        query: {
          ...this.controllingQueryParams,
          step: 'mfa',
          ...mfaParam,
        },
      });
    },

    patchSmsOtpLoginSession(patch: SmsOtpLoginSessionPatch) {
      const s = this.smsOtpLoginSession;

      if (patch.initialSendEverAttempted !== undefined) {
        s.initialSendEverAttempted = patch.initialSendEverAttempted;
      }

      if (patch.successfulSendIncrement) {
        s.successfulSendCount += patch.successfulSendIncrement;
      }

      if (patch.lastSessionId !== undefined) {
        s.lastSessionId = patch.lastSessionId;
      }

      if (patch.removedFromChooser !== undefined) {
        s.removedFromChooser = patch.removedFromChooser;
      }
    },

    onMfaError(error: unknown) {
      if (error instanceof RequestError) {
        const smsOtpEndsSession = error.statusCode === SMS_OTP_IDLE_TIMEOUT_STATUS_CODE
          || error.statusCode === SMS_OTP_VERIFY_RATE_LIMIT_STATUS_CODE;

        if (smsOtpEndsSession) {
          this.smsOtpLoginSession.removedFromChooser = true;
        }
      }

      const {
        message,
        action,
        displayDuration,
      } = ProcessPasswordResetMfaError(this.mfaType, error);
      this.errorMessageDisplayDuration = displayDuration;

      if (this.errorMessageDisplayDuration && this.errorMessageDisplayDuration > 0) {
        this.handleErrorCountdown();
      }

      if (action === 'exitToLogin') {
        this.handleResetForm();
        this.redirectToLogin(message);
      } else if (action === 'restart') {
        this.handleResetForm();
        this.redirectBackToResetStart(message);
      } else {
        this.setErrorMessage(message);
      }
    },

    onResetErrorMessage() {
      this.errorMessageDisplayDuration = undefined;
      this.setErrorMessage();
    },

    onSuccess() {
      routerService.goToUserLogin({ params: { success: passwordResetSuccess } });
    },

    redirectBackToResetStart(message?: string) {
      if (message) {
        this.setErrorMessage(message);
      }
      routerService.goToUserPasswordResetForm({
        query: {
          key: this.requestKey,
        },
      });
    },

    redirectToLogin(errorMessage = '') {
      routerService.goToUserLogin({
        params: { error: errorMessage },
      });
    },

    setErrorMessage(message = '') {
      this.errorMessage = message;
    },

    validateInitialStep() {
      const isInitialStepInvalid = this.step && !this.factors.length;

      if (isInitialStepInvalid) {
        routerService.replaceToUserLogin({ query: { ...this.controllingQueryParams } });
      }
    },
  },
});
